import { useEffect, useState } from 'react';
import { getKeyManagement, saveKeyAssignment, saveKeyType, GetKeyManagementOutputType } from 'zite-endpoints-sdk';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { Plus, Pencil, Key, AlertTriangle, Loader2, Settings } from 'lucide-react';
import { toast } from 'sonner';
import { format, parseISO } from 'date-fns';

type Assignment = GetKeyManagementOutputType['assignments'][0];
type KeyType = GetKeyManagementOutputType['keyTypes'][0];
type Employee = GetKeyManagementOutputType['employees'][0];

const fmtDate = (d?: string) => { try { return d ? format(parseISO(d.includes('T') ? d : d + 'T00:00:00'), 'dd MMM yyyy') : '—'; } catch { return d || '—'; } };

export default function KeyManagementTab() {
  const [assignments, setAssignments] = useState<Assignment[]>([]);
  const [keyTypes, setKeyTypes] = useState<KeyType[]>([]);
  const [employees, setEmployees] = useState<Employee[]>([]);
  const [loading, setLoading] = useState(true);
  const [assignOpen, setAssignOpen] = useState(false);
  const [editAssign, setEditAssign] = useState<Assignment | null>(null);
  const [keyTypeOpen, setKeyTypeOpen] = useState(false);
  const [saving, setSaving] = useState(false);

  // Assign form
  const [empId, setEmpId] = useState('');
  const [keyTypeId, setKeyTypeId] = useState('');
  const [qty, setQty] = useState(1);
  const [dateGiven, setDateGiven] = useState(new Date().toISOString().split('T')[0]);
  const [status, setStatus] = useState<'Active' | 'Lost' | 'Returned'>('Active');
  const [notes, setNotes] = useState('');
  const [replacementCost, setReplacementCost] = useState('');

  // Key type form
  const [ktName, setKtName] = useState('');
  const [ktMax, setKtMax] = useState(1);

  const load = () => {
    getKeyManagement({}).then(d => { setAssignments(d.assignments); setKeyTypes(d.keyTypes); setEmployees(d.employees); })
      .catch(() => toast.error('Failed to load')).finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  const openNew = () => {
    setEditAssign(null); setEmpId(''); setKeyTypeId(''); setQty(1);
    setDateGiven(new Date().toISOString().split('T')[0]); setStatus('Active'); setNotes(''); setReplacementCost('');
    setAssignOpen(true);
  };

  const openEdit = (a: Assignment) => {
    setEditAssign(a); setEmpId(a.employeeId); setKeyTypeId(a.keyTypeId); setQty(a.quantity);
    setDateGiven(a.dateGiven || ''); setStatus(a.status as any); setNotes(a.notes || '');
    setReplacementCost(a.replacementCost?.toString() || '');
    setAssignOpen(true);
  };

  const handleSaveAssignment = async () => {
    if (!empId || !keyTypeId) { toast.error('Employee and key type required'); return; }
    setSaving(true);
    try {
      await saveKeyAssignment({
        id: editAssign?.id,
        employeeId: empId, keyTypeId, quantity: qty, dateGiven, status, notes,
        replacementCost: replacementCost ? parseFloat(replacementCost) : undefined,
      });
      toast.success(editAssign ? 'Assignment updated' : 'Key assigned');
      setAssignOpen(false);
      load();
    } catch { toast.error('Failed to save'); }
    finally { setSaving(false); }
  };

  const handleSaveKeyType = async () => {
    if (!ktName.trim()) { toast.error('Key name is required'); return; }
    setSaving(true);
    try {
      await saveKeyType({ keyName: ktName, maxQuantity: ktMax });
      toast.success('Key type saved');
      setKeyTypeOpen(false);
      setKtName(''); setKtMax(1);
      load();
    } catch { toast.error('Failed to save'); }
    finally { setSaving(false); }
  };

  // Group assignments by employee
  const byEmployee = assignments.reduce((acc, a) => {
    if (!acc[a.employeeId]) acc[a.employeeId] = { name: a.employeeName, keys: [] };
    acc[a.employeeId].keys.push(a);
    return acc;
  }, {} as Record<string, { name: string; keys: Assignment[] }>);

  if (loading) return <div className="space-y-3">{[...Array(4)].map((_, i) => <Skeleton key={i} className="h-12" />)}</div>;

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <div>
          <h2 className="text-base font-semibold text-foreground flex items-center gap-2">
            <Key className="w-4 h-4 text-primary" /> Key Management
          </h2>
          <p className="text-xs text-muted-foreground mt-0.5">
            {assignments.filter(a => a.status === 'Active').length} active · {assignments.filter(a => a.status === 'Lost').length} lost
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={() => setKeyTypeOpen(true)}>
            <Settings className="w-3.5 h-3.5 mr-1.5" /> Key Types
          </Button>
          <Button size="sm" onClick={openNew}><Plus className="w-3.5 h-3.5 mr-1" /> Assign Key</Button>
        </div>
      </div>

      {keyTypes.length === 0 && (
        <div className="border border-amber-500/30 bg-amber-500/5 rounded-lg p-3 mb-4 text-xs text-amber-600">
          No key types defined. Add key types first (e.g. "Front Door", "Back Door", "Barn") before assigning keys.
        </div>
      )}

      <div className="border border-border rounded-lg overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-muted">
            <tr>
              <th className="px-4 py-2.5 text-left text-xs font-semibold text-muted-foreground">Employee</th>
              <th className="px-4 py-2.5 text-left text-xs font-semibold text-muted-foreground hidden sm:table-cell">Keys Held</th>
              <th className="px-4 py-2.5 text-left text-xs font-semibold text-muted-foreground hidden md:table-cell">Date Given</th>
              <th className="px-4 py-2.5 text-left text-xs font-semibold text-muted-foreground">Status</th>
              <th className="px-4 py-2.5" />
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {Object.entries(byEmployee).length === 0 ? (
              <tr><td colSpan={5} className="px-4 py-12 text-center text-sm text-muted-foreground">No keys assigned yet</td></tr>
            ) : (Object.entries(byEmployee) as [string, { name: string; keys: Assignment[] }][]).map(([empId, data]) => {
              const hasLost = data.keys.some(k => k.status === 'Lost');
              return (
                <tr key={empId}>
                  <td className="px-4 py-3 font-medium text-foreground">{data.name}</td>
                  <td className="px-4 py-3 hidden sm:table-cell">
                    <div className="flex flex-wrap gap-1">
                      {data.keys.map(k => (
                        <span key={k.id} className={`text-xs px-1.5 py-0.5 rounded ${k.status === 'Lost' ? 'bg-destructive/10 text-destructive' : 'bg-muted text-muted-foreground'}`}>
                          {k.keyTypeName}{k.status === 'Lost' ? ' — LOST' : ''}
                        </span>
                      ))}
                    </div>
                  </td>
                  <td className="px-4 py-3 text-muted-foreground hidden md:table-cell">{fmtDate(data.keys[0]?.dateGiven)}</td>
                  <td className="px-4 py-3">
                    {hasLost ? (
                      <span className="text-xs bg-destructive/10 text-destructive px-2 py-0.5 rounded flex items-center gap-1 w-fit">
                        <AlertTriangle className="w-3 h-3" /> {data.keys.filter(k => k.status === 'Lost').length} Lost
                      </span>
                    ) : (
                      <span className="text-xs bg-primary/10 text-primary px-2 py-0.5 rounded">Active</span>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    {data.keys.map(k => (
                      <Button key={k.id} size="sm" variant="ghost" className="h-7 text-xs" onClick={() => openEdit(k)}>
                        <Pencil className="w-3 h-3 mr-1" /> Edit
                      </Button>
                    ))}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Assign/Edit Sheet */}
      <Sheet open={assignOpen} onOpenChange={setAssignOpen}>
        <SheetContent className="w-full sm:max-w-md overflow-y-auto">
          <SheetHeader><SheetTitle>{editAssign ? 'Edit Key Assignment' : 'Assign Key'}</SheetTitle></SheetHeader>
          <div className="space-y-4 mt-6">
            <div>
              <label className="text-xs text-muted-foreground uppercase tracking-wider mb-1.5 block">Employee *</label>
              <Select value={empId} onValueChange={setEmpId}>
                <SelectTrigger><SelectValue placeholder="Select employee" /></SelectTrigger>
                <SelectContent>{employees.map(e => <SelectItem key={e.id} value={e.id}>{e.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-xs text-muted-foreground uppercase tracking-wider mb-1.5 block">Key Type *</label>
              <Select value={keyTypeId} onValueChange={setKeyTypeId}>
                <SelectTrigger><SelectValue placeholder="Select key type" /></SelectTrigger>
                <SelectContent>{keyTypes.map(k => <SelectItem key={k.id} value={k.id}>{k.keyName}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs text-muted-foreground uppercase tracking-wider mb-1.5 block">Quantity</label>
                <Input type="number" min={1} value={qty} onChange={e => setQty(parseInt(e.target.value) || 1)} />
              </div>
              <div>
                <label className="text-xs text-muted-foreground uppercase tracking-wider mb-1.5 block">Date Given</label>
                <Input type="date" value={dateGiven} onChange={e => setDateGiven(e.target.value)} />
              </div>
            </div>
            <div>
              <label className="text-xs text-muted-foreground uppercase tracking-wider mb-1.5 block">Status</label>
              <Select value={status} onValueChange={v => setStatus(v as any)}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="Active">Active</SelectItem>
                  <SelectItem value="Lost">Lost</SelectItem>
                  <SelectItem value="Returned">Returned</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {status === 'Lost' && (
              <div>
                <label className="text-xs text-muted-foreground uppercase tracking-wider mb-1.5 block">Replacement Cost (£)</label>
                <Input type="number" value={replacementCost} onChange={e => setReplacementCost(e.target.value)} placeholder="0.00" step="0.01" />
              </div>
            )}
            <div>
              <label className="text-xs text-muted-foreground uppercase tracking-wider mb-1.5 block">Notes</label>
              <Input value={notes} onChange={e => setNotes(e.target.value)} placeholder="Any notes..." />
            </div>
            <Button onClick={handleSaveAssignment} disabled={saving} className="w-full">
              {saving ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Saving...</> : editAssign ? 'Update Assignment' : 'Assign Key'}
            </Button>
          </div>
        </SheetContent>
      </Sheet>

      {/* Key Types Dialog */}
      <Dialog open={keyTypeOpen} onOpenChange={setKeyTypeOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Manage Key Types</DialogTitle></DialogHeader>
          <div className="space-y-4 pt-2">
            <div className="border border-border rounded-lg divide-y divide-border">
              {keyTypes.length === 0 ? (
                <p className="px-4 py-4 text-sm text-muted-foreground">No key types yet</p>
              ) : keyTypes.map(k => (
                <div key={k.id} className="px-4 py-2.5 flex items-center justify-between">
                  <span className="text-sm text-foreground">{k.keyName}</span>
                  <span className="text-xs text-muted-foreground">Max {k.maxQuantity}</span>
                </div>
              ))}
            </div>
            <div className="border-t border-border pt-4">
              <p className="text-xs font-semibold text-foreground mb-3">Add New Key Type</p>
              <div className="flex gap-2">
                <Input value={ktName} onChange={e => setKtName(e.target.value)} placeholder="e.g. Front Door" className="flex-1" />
                <Input type="number" value={ktMax} onChange={e => setKtMax(parseInt(e.target.value) || 1)} className="w-20" min={1} />
                <Button onClick={handleSaveKeyType} disabled={saving || !ktName.trim()} size="sm">
                  {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
                </Button>
              </div>
              <p className="text-xs text-muted-foreground mt-1.5">Default key types: Front Door, Back Door, Bottle Store, Emergency Exit, Barn</p>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
